import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import LeftPanel from './components/LeftPanel';
import RightPanel from './components/RightPanel';
import MainPageSideBar from './components/MainPageSideBar';
import LoginPage from './components/LoginPage';
import SetPassword from './components/SetPassword';
import RegistrationPage from './components/RegistrationPage';
import ProfilePage from './components/ProfilePage';
import UserManagementPage from './components/UserManagementPage';
import { authService } from './services/authService';
import { ProjekttervezesFocus } from './services/projekttervezesService';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3a4651', // dark blue-gray
    },
    background: {
      default: '#f0f3f6', // light blue-gray
    },
  },
});

type ViewType = 'main' | 'login' | 'registration' | 'profile' | 'usermanagement' | 'set-password';

const App: React.FC = () => {
  const [selectedMenu, setSelectedMenu] = useState<string | null>('dashboard');
  const [currentView, setCurrentView] = useState<ViewType>('login');
  const [authSidebarOpen, setAuthSidebarOpen] = useState(false);
  const [firstTimeEmail, setFirstTimeEmail] = useState('');
  const [selectedMunkaszam, setSelectedMunkaszam] = useState<string | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<string | null>(null);
  const [selectedFolyamatlapMunkaszam, setSelectedFolyamatlapMunkaszam] = useState<string | null>(null);
  const [projekttervezesFocus, setProjekttervezesFocus] = useState<ProjekttervezesFocus | null>(null);

  // Check and validate login status on app start
  useEffect(() => {
    const validateUserSession = async () => {
      if (authService.isLoggedIn()) {
        // Validate session with backend
        const isValid = await authService.validateSession();
        if (isValid) {
          setCurrentView('main');
        } else {
          // Session is invalid, go to login
          setCurrentView('login');
        }
      }
    };

    validateUserSession();
  }, []);

  const handleAuthClick = () => {
    setAuthSidebarOpen(true);
  };

  const handleAuthSidebarClose = () => {
    setAuthSidebarOpen(false);
  };

  const handleSelectAuth = async (authType: 'profile' | 'logout' | 'usermanagement' | 'backtomain') => {
    if (authType === 'logout') {
      try {
        await authService.logout();
        setCurrentView('login');
        setSelectedMenu(null);
        setAuthSidebarOpen(false);
      } catch (error) {
        console.error('Logout error:', error);
        // Force logout even if API call fails
        setCurrentView('login');
        setSelectedMenu(null);
        setAuthSidebarOpen(false);
      }
    } else if (authType === 'profile') {
      setCurrentView('profile');
    } else if (authType === 'usermanagement') {
      setCurrentView('usermanagement');
    } else if (authType === 'backtomain') {
      setCurrentView('main');
    }
  };

  const handleNavigateToRegistration = () => {
    setCurrentView('registration');
  };

  const handleBackToLogin = () => {
    setCurrentView('login');
  };

  const handleLogout = () => {
    setCurrentView('login');
    setSelectedMenu(null);
    setAuthSidebarOpen(false);
  };

  const handleBackToMain = () => {
    setCurrentView('main');
    setSelectedMenu(null);
  };

  const handleNavigateToMain = () => {
    setCurrentView('main');
  };

  const handleNavigateToFirstTimeLogin = (email: string) => {
    setFirstTimeEmail(email);
    setCurrentView('set-password');
  };

  const handlePasswordSet = () => {
    setCurrentView('login');
    setFirstTimeEmail('');
  };

  const handleNavigateToIdoraforditas = (munkaszam: string) => {
    setSelectedMunkaszam(munkaszam);
    setSelectedParticipant(null); // Clear participant when navigating without specific user
    setSelectedMenu('idoraforditasok');
  };

  const handleNavigateToParticipantTimeTracking = (participantName: string, munkaszam: string) => {
    setSelectedMunkaszam(munkaszam);
    setSelectedParticipant(participantName);
    setSelectedMenu('idoraforditasok');
  };

  const handleMunkaszamCleared = () => {
    setSelectedMunkaszam(null);
    setSelectedParticipant(null);
  };

  const handleNavigateToFolyamatlap = (munkaszam: string) => {
    setSelectedFolyamatlapMunkaszam(munkaszam);
    setSelectedMenu('folyamatlap');
  };

  const handleBackToMunkaszamokFromFolyamatlap = () => {
    setSelectedMenu('munkaszamok');
  };

  const handleNavigateToProjekttervezesFeladat = (focus: ProjekttervezesFocus) => {
    setProjekttervezesFocus(focus);
    setSelectedMenu('projekttervezes');
  };

  const handleProjekttervezesFocusHandled = () => {
    setProjekttervezesFocus(null);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'login':
        return (
          <LoginPage 
            onNavigateToRegistration={handleNavigateToRegistration}
            onNavigateToMain={handleNavigateToMain}
            onNavigateToFirstTimeLogin={handleNavigateToFirstTimeLogin}
          />
        );
      case 'set-password':
        return (
          <SetPassword
            email={firstTimeEmail}
            onPasswordSet={handlePasswordSet}
            onBack={handleBackToLogin}
          />
        );
      case 'registration':
        return <RegistrationPage onBack={handleBackToLogin} onNavigateToLogin={handleBackToLogin} />;
      case 'profile':
        return <ProfilePage onBack={handleBackToMain} />;
      case 'usermanagement':
        return <UserManagementPage onBack={handleBackToMain} onAuthClick={handleAuthClick} />;
      default:
        return (
          <Box sx={{ display: 'flex', height: '100vh' }}>
            <LeftPanel selectedMenu={selectedMenu} setSelectedMenu={setSelectedMenu} />
            <RightPanel 
              selectedMenu={selectedMenu} 
              onAuthClick={handleAuthClick}
              selectedMunkaszam={selectedMunkaszam}
              selectedParticipant={selectedParticipant}
              selectedFolyamatlapMunkaszam={selectedFolyamatlapMunkaszam}
              onMunkaszamCleared={handleMunkaszamCleared}
              onNavigateToIdoraforditas={handleNavigateToIdoraforditas}
              onNavigateToParticipantTimeTracking={handleNavigateToParticipantTimeTracking}
              onNavigateToFolyamatlap={handleNavigateToFolyamatlap}
              onBackToMunkaszamokFromFolyamatlap={handleBackToMunkaszamokFromFolyamatlap}
              projekttervezesFocus={projekttervezesFocus}
              onNavigateToProjekttervezesFeladat={handleNavigateToProjekttervezesFeladat}
              onProjekttervezesFocusHandled={handleProjekttervezesFocusHandled}
            />
          </Box>
        );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box 
        sx={{ 
          position: 'relative',
          opacity: authSidebarOpen && currentView === 'main' ? 0.75 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        {renderCurrentView()}
      </Box>
      
      {(currentView === 'main' || currentView === 'usermanagement') && (
        <MainPageSideBar
          open={authSidebarOpen}
          onClose={handleAuthSidebarClose}
          onSelectAuth={handleSelectAuth}
          currentView={currentView}
        />
      )}
    </ThemeProvider>
  );
};

export default App;
